import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Link, useParams } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { Button } from '@arcflight/tf-component-library';
import { ButtonSize } from '../../components/PaginatedDefectsTable/DefectTableHeader';
import InnerMenuLayoutNew from '../../layouts/InnerMenuLayoutNew';
import TFCard from '../../components/TFCard/TFCard';
import { addToast, ToastCategories, ToastTypes } from '../../models/toasts';
import { add } from '../../models/userSettings/actions';
import { AircraftActionTypes } from '../../models/aircraft';
import useGetOperatorSetting from '../../utils/useGetOperatorSetting';
import useGetAircraft from '../../services/hooks/aircraft/useGetAircraft';
import useGetAirports from '../../services/hooks/aircraft/useGetAirports';
import rightChevron from '../../assets/right-chevron.svg';
import TFLoading from '../../components/TFLoading';
import AircraftCard from './AircraftCard';
import AircraftOverviewTiles from './AircraftOverviewTiles';
import RoutesMap from './RoutesMap';
import TypedOverviewRecentTrips from './TypedOverviewRecentTrips';

const GridWrapper = styled.div`
  display: grid;
  grid-template-columns: 2fr 3fr;
  gap: 20px;
  margin: 20px 0;
  @media (max-width: 451px) {
    grid-template-columns: 1fr;
  }
`;

const RecentTripsHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ViewAllText = styled.div`
  height: 26px;
  font-size: 14px;
  font-weight: 500;
  letter-spacing: -0.09px;
  color: #126fd6;
  padding-right: 4px;
  align-content: center;
`;

const ButtonTextWrapper = styled.div`
  display: flex;
  align-items: center;
  img {
    margin-top: 2px;
  }
`;

const Overview: React.FC = () => {
  const [isAircraftTotalsVisible, setIsAircraftTotalsVisible] = useState(false);

  const { id } = useParams<{ id: string }>();
  const dispatch = useDispatch();
  const { formatMessage } = useIntl();

  const dentAndBuckleTitle = useGetOperatorSetting('dent_and_buckle', id);
  const eropsTitle = useGetOperatorSetting('erops', id);
  const showGoogleMaps = useGetOperatorSetting('show_google_maps', id);

  const { data: aircraftData, isFetching: aircraftLoading, isError: aircraftError } = useGetAircraft(id);
  const { data: airportData, isFetching: airportLoading, isError: airportError } =
    useGetAirports(aircraftData?.departure_airport?.ident);

  const handleShowAircraftTotalsClick = () => {
    setIsAircraftTotalsVisible(!isAircraftTotalsVisible);
    dispatch(add({ payload: { aircraft_totals_is_visible: !isAircraftTotalsVisible, type: 'totals' } }));
  };

  if (aircraftError || airportError) {
    const title = aircraftError ?
      formatMessage({ id: 'text.thereWasAnErrorSourcingAircraft' }) :
      formatMessage({ id: 'text.thereWasAnErrorSourcingAirport' });
    const message = aircraftError ?
      formatMessage({ id: 'text.thereWasAnErrorSourcingAircraft' }) :
      formatMessage({ id: 'text.thereWasAnErrorSourcingAirport' });
    dispatch(addToast({
      payload: {
        title,
        message,
        type: ToastTypes.ERROR,
        category: ToastCategories.FLAG,
      },
    }));
  }

  useEffect(() => {
    if (aircraftData) {
      dispatch({
        type: AircraftActionTypes.SAVE,
        payload: aircraftData,
      });
    }
  }, [aircraftData, dispatch]);

  return (
    <InnerMenuLayoutNew>
      <TFLoading loading={aircraftLoading || airportLoading} />
      {aircraftData?.id && (
        <div>
          <TFCard>
            <AircraftCard
              aircraft={aircraftData}
              currentAirport={airportData?.[0]}
              isAircraftTotalsVisible={isAircraftTotalsVisible}
              handleShowAircraftTotalsClick={handleShowAircraftTotalsClick}
            />
          </TFCard>
          <GridWrapper>
            <TFCard>
              <AircraftOverviewTiles
                aircraft={aircraftData}
                dentAndBuckleTitle={dentAndBuckleTitle}
                eropsTitle={eropsTitle}
              />
            </TFCard>
            <TFCard>
              <RecentTripsHeader>
                {formatMessage({ id: 'title.recentTrips' })}
                <Link
                  to={aircraftData ? `/aircraft/${aircraftData.id}/trips` : ''}
                >
                  <Button size={ButtonSize.MEDIUM} primary={false} onClick={null}>
                    <ButtonTextWrapper>
                      <ViewAllText>
                        {formatMessage({ id: 'title.viewAll' })}
                      </ViewAllText>
                      <img src={rightChevron} alt="chevron" />
                    </ButtonTextWrapper>
                  </Button>
                </Link>
              </RecentTripsHeader>
              <TypedOverviewRecentTrips aircraft={aircraftData} />
            </TFCard>
          </GridWrapper>
          {(showGoogleMaps ?? true) ? <RoutesMap aircraftId={id} currentAirport={airportData?.[0]} /> : null}
        </div>
      )}
    </InnerMenuLayoutNew>
  );
};

export default Overview;
